import { useEffect, useState } from 'react';
import { api } from '../lib/api';

type Task = {
  _id: string;
  title: string;
  description?: string;
  status: 'open' | 'in_progress' | 'completed' | 'cancelled';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  dueAt?: string | null;
  assignedTo?: string | null;
  createdAt: string;
};

type Filter = 'all' | 'mine' | 'open' | 'overdue';

export default function Tasks() {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [filter, setFilter] = useState<Filter>('all');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState<Task['priority']>('normal');
  const [dueAt, setDueAt] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      let url = '/api/tasks';
      if (filter === 'mine') url = '/api/tasks/assigned-to-me';
      if (filter === 'open') url = '/api/tasks?status=open';
      if (filter === 'overdue') url = `/api/tasks?status=open&dueBefore=${new Date().toISOString()}`;
      const { data } = await api.get(url);
      setTasks(data.tasks || []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  async function createTask(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    try {
      await api.post('/api/tasks', {
        title: title.trim(),
        priority,
        dueAt: dueAt ? new Date(dueAt).toISOString() : null,
      });
      setTitle('');
      setDueAt('');
      setPriority('normal');
      setCreating(false);
      load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to create');
    }
  }

  async function complete(id: string) {
    await api.post(`/api/tasks/${id}/complete`);
    load();
  }

  async function reopen(id: string) {
    await api.post(`/api/tasks/${id}/reopen`);
    load();
  }

  async function remove(id: string) {
    if (!confirm('Delete this task?')) return;
    await api.delete(`/api/tasks/${id}`);
    load();
  }

  return (
    <main className="text-white">
      <section className="max-w-5xl mx-auto px-8 py-10">
        <h1 className="h-display text-3xl mb-6">Tasks</h1>
        <div className="flex items-center justify-between mb-6">
          <div className="flex gap-2">
            {(['all', 'mine', 'open', 'overdue'] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1 rounded-full text-xs uppercase tracking-wider border ${
                  filter === f
                    ? 'bg-dgrp-red text-white border-dgrp-red'
                    : 'border-dgal-navy-2 text-dgal-mist hover:text-white'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
          <button
            onClick={() => setCreating((c) => !c)}
            className="px-4 py-2 rounded bg-dgrp-red text-white text-sm font-semibold hover:bg-dgrp-red-hover"
          >
            {creating ? 'Cancel' : '+ New Task'}
          </button>
        </div>

        {creating && (
          <form onSubmit={createTask} className="bg-dgal-navy/60 border border-dgrp-red/20 rounded-lg p-4 mb-6 grid gap-3">
            <input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Task title"
              className="bg-dgal-ink border border-dgal-navy-2 rounded px-3 py-2"
            />
            <div className="flex gap-3">
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value as Task['priority'])}
                className="bg-dgal-ink border border-dgal-navy-2 rounded px-3 py-2"
              >
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
              <input
                type="datetime-local"
                value={dueAt}
                onChange={(e) => setDueAt(e.target.value)}
                className="bg-dgal-ink border border-dgal-navy-2 rounded px-3 py-2"
              />
              <button
                type="submit"
                className="ml-auto px-4 py-2 rounded bg-dgrp-red text-white font-semibold hover:bg-dgrp-red-hover"
              >
                Create
              </button>
            </div>
          </form>
        )}

        {error && <div className="text-red-400 text-sm mb-4">{error}</div>}
        {loading ? (
          <div className="text-dgal-mist">Loading…</div>
        ) : tasks.length === 0 ? (
          <div className="text-dgal-mist">No tasks.</div>
        ) : (
          <ul className="divide-y divide-dgal-navy-2 border border-dgal-navy-2 rounded-lg overflow-hidden">
            {tasks.map((t) => (
              <li key={t._id} className="flex items-center gap-4 px-4 py-3 bg-dgal-navy/40">
                <input
                  type="checkbox"
                  checked={t.status === 'completed'}
                  onChange={() => (t.status === 'completed' ? reopen(t._id) : complete(t._id))}
                  className="h-4 w-4 accent-dgrp-red"
                />
                <div className="flex-1">
                  <div className={`font-medium ${t.status === 'completed' ? 'line-through text-dgal-mist' : ''}`}>
                    {t.title}
                  </div>
                  <div className="text-xs text-dgal-mist mt-1">
                    <span className="uppercase tracking-wider mr-3">{t.priority}</span>
                    {t.dueAt && <span>Due {new Date(t.dueAt).toLocaleString()}</span>}
                  </div>
                </div>
                <span className="text-xs text-dg-text-2 uppercase tracking-wider">{t.status.replace('_', ' ')}</span>
                <button onClick={() => remove(t._id)} className="text-xs text-dgal-mist hover:text-red-400">
                  Delete
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </main>
  );
}
